import React, { useEffect, useState, useRef } from "react";
import { BsStars, BsCheckCircleFill } from "react-icons/bs";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { FiZap, FiRefreshCw } from "react-icons/fi";

/* ─── Inject styles once ─── */
(() => {
  if (document.getElementById("sai-styles")) return;
  const el = document.createElement("style");
  el.id = "sai-styles";
  el.textContent = `

    .sai-overlay {
      position: fixed; inset: 0; z-index: 9999;
      background: rgba(10,0,5,0.55);
      backdrop-filter: blur(6px);
      display: flex; align-items: center; justify-content: center;
      animation: sai-fade-in 0.22s ease;
      font-family: 'DM Sans', sans-serif;
    }
    @keyframes sai-fade-in { from{opacity:0} to{opacity:1} }
    @keyframes sai-slide-up {
      from{opacity:0;transform:translateY(28px) scale(0.97)}
      to{opacity:1;transform:translateY(0) scale(1)}
    }
    @keyframes sai-spin { to{transform:rotate(360deg)} }
    @keyframes sai-pulse { 0%,100%{opacity:1} 50%{opacity:0.45} }
    @keyframes sai-bar {
      0%{width:0%} 100%{width:100%}
    }
    @keyframes sai-pop {
      from{opacity:0;transform:scale(0.88) translateY(6px)}
      to{opacity:1;transform:scale(1) translateY(0)}
    }
    @keyframes sai-shimmer {
      0%{background-position:-400px 0}
      100%{background-position:400px 0}
    }
    @keyframes sai-check-pop {
      0%{transform:scale(0)} 70%{transform:scale(1.2)} 100%{transform:scale(1)}
    }

    .sai-card {
      background: #fff;
      border-radius: 22px;
      width: min(560px, 96vw);
      box-shadow: 0 24px 80px rgba(160,0,48,0.14), 0 2px 8px rgba(0,0,0,0.08);
      overflow: hidden;
      animation: sai-slide-up 0.32s cubic-bezier(0.34,1.56,0.64,1);
      border: 1.5px solid rgba(160,0,48,0.12);
    }

    /* ── Header ── */
    .sai-header {
      background: #a00030;
      padding: 20px 22px 18px;
      display: flex; align-items: center; justify-content: space-between;
      position: relative; overflow: hidden;
    }
    .sai-header::after {
      content:'';
      position:absolute; inset:0;
      background: linear-gradient(135deg,rgba(255,255,255,0.08) 0%,transparent 60%);
      pointer-events:none;
    }
    .sai-header-left { display:flex; align-items:center; gap:12px; }
    .sai-header-icon {
      width:40px; height:40px; border-radius:12px;
      background:rgba(255,255,255,0.18);
      display:flex; align-items:center; justify-content:center;
      flex-shrink:0;
    }
    .sai-header-title {
      font-family:'Playfair Display',serif;
      font-size:17px; font-weight:700; color:#fff; margin:0; line-height:1.2;
    }
    .sai-header-sub { font-size:11px; color:rgba(255,255,255,0.72); margin:3px 0 0; font-weight:500; }
    .sai-close-btn {
      background:rgba(255,255,255,0.15); border:none; border-radius:50%;
      width:30px; height:30px; display:flex; align-items:center; justify-content:center;
      cursor:pointer; color:#fff; flex-shrink:0; transition:background 0.15s;
      position:relative; z-index:1;
    }
    .sai-close-btn:hover { background:rgba(255,255,255,0.28); }

    /* ── Progress bar ── */
    .sai-progress-track {
      height:3px; background:rgba(160,0,48,0.1);
    }
    .sai-progress-fill {
      height:100%; background:#a00030;
      transition:width 0.4s ease;
    }

    /* ── Body ── */
    .sai-body { padding:22px; }

    /* ── Step pill ── */
    .sai-step-pill {
      display:inline-flex; align-items:center; gap:6px;
      background:#fff3f6; color:#a00030;
      border:1px solid rgba(160,0,48,0.2);
      border-radius:99px; padding:4px 12px;
      font-size:11px; font-weight:700;
      text-transform:uppercase; letter-spacing:0.06em;
      margin-bottom:14px;
    }
    .sai-step-dot {
      width:6px; height:6px; border-radius:50%;
      background:#a00030;
      animation: sai-pulse 1.4s infinite;
    }

    /* ── Question ── */
    .sai-question {
      font-size:16px; font-weight:700; color:#111827;
      margin:0 0 6px; line-height:1.4;
      font-family:'Playfair Display',serif;
    }
    .sai-question-hint {
      font-size:12px; color:#9ca3af; margin:0 0 16px; font-weight:500;
    }

    /* ── Input ── */
    .sai-textarea {
      width:100%; border:1.5px solid #e8eaf0; border-radius:12px;
      padding:12px 14px; font-size:14px; color:#1f2937;
      font-family:'DM Sans',sans-serif; resize:none; outline:none;
      transition:border-color 0.18s, box-shadow 0.18s;
      background:#fafafa; line-height:1.6; box-sizing:border-box;
    }
    .sai-textarea:focus {
      border-color:#a00030;
      box-shadow:0 0 0 3px rgba(160,0,48,0.08);
      background:#fff;
    }
    .sai-textarea::placeholder { color:#c4c9d4; }

    /* ── Single input ── */
    .sai-input {
      width:100%; border:1.5px solid #e8eaf0; border-radius:10px;
      padding:10px 14px; font-size:14px; color:#1f2937;
      font-family:'DM Sans',sans-serif; outline:none;
      transition:border-color 0.18s, box-shadow 0.18s;
      background:#fafafa; box-sizing:border-box;
    }
    .sai-input:focus {
      border-color:#a00030;
      box-shadow:0 0 0 3px rgba(160,0,48,0.08);
      background:#fff;
    }

    /* ── Char count ── */
    .sai-char { font-size:11px; color:#c4c9d4; text-align:right; margin:5px 0 0; }
    .sai-char.warn { color:#e63946; }

    /* ── Action row ── */
    .sai-actions {
      display:flex; align-items:center; justify-content:space-between;
      margin-top:18px; gap:10px;
    }
    .sai-skip-btn {
      background:none; border:none; color:#9ca3af;
      font-size:13px; font-weight:600; cursor:pointer;
      padding:0; font-family:'DM Sans',sans-serif;
      transition:color 0.15s;
    }
    .sai-skip-btn:hover { color:#6b7280; }
    .sai-next-btn {
      display:inline-flex; align-items:center; gap:7px;
      background:#a00030; color:#fff; border:none; border-radius:10px;
      padding:10px 22px; font-size:14px; font-weight:700;
      cursor:pointer; font-family:'DM Sans',sans-serif;
      transition:background 0.18s, transform 0.15s, box-shadow 0.18s;
      box-shadow:0 4px 14px rgba(160,0,48,0.25);
    }
    .sai-next-btn:hover:not(:disabled) {
      background:#880028; transform:translateY(-1px);
      box-shadow:0 6px 20px rgba(160,0,48,0.32);
    }
    .sai-next-btn:disabled {
      background:#e5e7eb; color:#9ca3af;
      box-shadow:none; cursor:not-allowed;
    }

    /* ── Loading state ── */
    .sai-loading-box {
      text-align:center; padding:10px 0 6px;
    }
    .sai-spinner {
      width:44px; height:44px; border-radius:50%;
      border:3px solid rgba(160,0,48,0.12);
      border-top:3px solid #a00030;
      animation:sai-spin 0.85s linear infinite;
      margin:0 auto 16px;
    }
    .sai-loading-title {
      font-size:15px; font-weight:700; color:#111827; margin:0 0 5px;
      font-family:'Playfair Display',serif;
    }
    .sai-loading-sub { font-size:12px; color:#9ca3af; margin:0 0 20px; }
    .sai-loading-bar-track {
      height:4px; background:#f3f4f6; border-radius:99px; overflow:hidden;
      margin-bottom:16px;
    }
    .sai-loading-bar-fill {
      height:100%; background:linear-gradient(90deg,#a00030,#e0004a);
      border-radius:99px;
      animation:sai-bar 2.2s ease-in-out infinite;
    }
    .sai-preview-pills {
      display:flex; flex-wrap:wrap; gap:7px; justify-content:center;
    }
    .sai-preview-pill {
      padding:4px 12px; border-radius:99px;
      background:#fff3f6; border:1px solid rgba(160,0,48,0.18);
      font-size:11px; color:#a00030; font-weight:600;
      animation:sai-pop 0.3s ease both;
    }

    /* ── Success state ── */
    .sai-success-box { text-align:center; padding:10px 0 4px; }
    .sai-success-icon {
      color:#a00030; font-size:44px;
      animation:sai-check-pop 0.4s cubic-bezier(0.34,1.56,0.64,1);
      display:block; margin:0 auto 14px;
    }
    .sai-success-title {
      font-size:17px; font-weight:700; color:#111827; margin:0 0 6px;
      font-family:'Playfair Display',serif;
    }
    .sai-success-sub { font-size:13px; color:#6b7280; margin:0 0 20px; }
    .sai-success-pills {
      display:flex; flex-wrap:wrap; gap:7px; justify-content:center; margin-bottom:20px;
    }
    .sai-success-pill {
      padding:5px 13px; border-radius:99px;
      background:#fff3f6; border:1px solid rgba(160,0,48,0.22);
      font-size:12px; color:#a00030; font-weight:600;
      animation:sai-pop 0.35s ease both;
    }
    .sai-done-btn {
      display:inline-flex; align-items:center; gap:7px;
      background:#a00030; color:#fff; border:none; border-radius:10px;
      padding:11px 28px; font-size:14px; font-weight:700;
      cursor:pointer; font-family:'DM Sans',sans-serif;
      box-shadow:0 4px 14px rgba(160,0,48,0.28);
      transition:background 0.18s;
    }
    .sai-done-btn:hover { background:#880028; }

    /* ── Regen btn ── */
    .sai-regen-btn {
      display:inline-flex; align-items:center; gap:6px;
      background:#fff3f6; color:#a00030;
      border:1.5px solid rgba(160,0,48,0.22); border-radius:8px;
      padding:7px 14px; font-size:12px; font-weight:600;
      cursor:pointer; font-family:'DM Sans',sans-serif;
      transition:all 0.15s;
    }
    .sai-regen-btn:hover { background:#ffe4ec; }

    /* ── What we generate info ── */
    .sai-info-cards {
      display:flex; gap:10px; margin-bottom:18px;
    }
    .sai-info-card {
      flex:1; background:#fafafa; border:1px solid #f0f1f5;
      border-radius:10px; padding:10px 12px;
    }
    .sai-info-card-icon { font-size:18px; margin-bottom:5px; }
    .sai-info-card-label { font-size:11px; font-weight:700; color:#374151; margin:0 0 2px; }
    .sai-info-card-desc { font-size:10px; color:#9ca3af; margin:0; }
  `;
  document.head.appendChild(el);
})();

// ─── Keyword → preview features map ───────────────────────────────────────────
const PREVIEW_MAP = [
  { keys: ["video","call","consult"],        feats: ["HD Video Calling","Screen Sharing","Session Recording","Real-time Chat","Multi-user Support"] },
  { keys: ["web","develop","site","design"],  feats: ["Responsive Design","SEO Optimization","Admin Dashboard","Payment Integration","API Integration"] },
  { keys: ["subscri","platform","saas"],      feats: ["Monthly Plans","Secure Payments","Email Notifications","Analytics Dashboard","User Management"] },
  { keys: ["mentor","coach","train"],         feats: ["1-on-1 Sessions","Expert Guidance","Goal Tracking","Resource Sharing","Follow-up Support"] },
  { keys: ["market","seo","content","brand"], feats: ["SEO Strategy","Content Calendar","Social Media Plan","Analytics Reports","Brand Positioning"] },
  { keys: ["health","well","fit","yoga"],     feats: ["Personalized Plan","Progress Tracking","Nutrition Advice","Weekly Check-ins","24/7 Support"] },
  { keys: ["legal","law","compli"],           feats: ["Legal Consultation","Document Review","Risk Assessment","Compliance Check","Follow-up Sessions"] },
  { keys: ["finance","invest","account"],     feats: ["Portfolio Review","Tax Planning","Investment Strategy","Risk Assessment","Monthly Reports"] },
];
const DEFAULT_FEATS = ["Personalized Approach","Expert Guidance","Flexible Scheduling","Progress Tracking","Results Focused"];

function getPreviewFeats(name = "") {
  const lower = name.toLowerCase();
  const match = PREVIEW_MAP.find(m => m.keys.some(k => lower.includes(k)));
  return match ? match.feats : DEFAULT_FEATS;
}

// ─── STEPS config ─────────────────────────────────────────────────────────────
// Only 2 lightweight questions before generating — fast & effective
const STEPS = [
  {
    id: "audience",
    question: "Who is your ideal client?",
    hint: "e.g. Startup founders, working professionals, students…",
    placeholder: "Describe your target audience in a few words",
    type: "text",
    maxLen: 120,
  },
  {
    id: "outcome",
    question: "What's the #1 result clients get?",
    hint: "e.g. Land their first client, lose 10kg, launch their MVP…",
    placeholder: "The key outcome or transformation you deliver",
    type: "text",
    maxLen: 150,
  },
];

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const ServiceProfileWizardCard = ({
  visible,
  onClose,
  serviceName,
  loading,
  isSummary,
  summaryText,
  // Legacy props kept for backward compat (not used in new flow)
  question, step, totalSteps, input, setInput, onNext,
}) => {
  const [isVisible, setIsVisible]   = useState(false);
  const [stepIdx, setStepIdx]       = useState(0);       // 0 | 1 | "generating" | "done"
  const [answers, setAnswers]       = useState({});
  const [currentVal, setCurrentVal] = useState("");
  const inputRef = useRef(null);

  const previewFeats = getPreviewFeats(serviceName);
  const totalUserSteps = STEPS.length;
  const isGenerating = stepIdx === "generating";
  const isDone       = stepIdx === "done" || isSummary;
  const currentStep  = typeof stepIdx === "number" ? STEPS[stepIdx] : null;

  // progress 0→1
  const progress = isDone ? 1
    : isGenerating ? 0.85
    : typeof stepIdx === "number" ? (stepIdx / totalUserSteps) * 0.8
    : 0;

  useEffect(() => {
    if (visible) { setIsVisible(true); setStepIdx(0); setAnswers({}); setCurrentVal(""); }
    else { const t = setTimeout(() => setIsVisible(false), 280); return () => clearTimeout(t); }
  }, [visible]);

  // When external loading flips to done, move to done state
  useEffect(() => {
    if (isSummary && stepIdx === "generating") setStepIdx("done");
  }, [isSummary]);

  // Auto-focus input
  useEffect(() => {
    if (visible && typeof stepIdx === "number") {
      setTimeout(() => inputRef.current?.focus(), 120);
    }
  }, [stepIdx, visible]);

  if (!isVisible && !visible) return null;

  const handleNext = () => {
    if (!currentVal.trim()) return;
    const newAnswers = { ...answers, [currentStep.id]: currentVal.trim() };
    setAnswers(newAnswers);
    setCurrentVal("");

    if (stepIdx < totalUserSteps - 1) {
      setStepIdx(s => s + 1);
    } else {
      // All questions answered — trigger generation
      setStepIdx("generating");
      // Call onNext with collected context so parent can build better prompt
      if (onNext) onNext(newAnswers);
    }
  };

  const handleSkip = () => {
    const newAnswers = { ...answers, [currentStep?.id]: "" };
    setAnswers(newAnswers);
    setCurrentVal("");
    if (stepIdx < totalUserSteps - 1) {
      setStepIdx(s => s + 1);
    } else {
      setStepIdx("generating");
      if (onNext) onNext(newAnswers);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && currentStep?.type !== "textarea") {
      e.preventDefault();
      handleNext();
    }
  };

  const handleDone = () => {
    onClose?.();
  };

  // ── Parse generated features from summaryText for display ──
  const parsedFeats = (() => {
    if (!summaryText) return [];
    const m = summaryText.match(/FEATURES:\s*(.+)/i);
    if (!m) return [];
    return m[1].split("|").map(f => f.trim()).filter(Boolean).slice(0, 5);
  })();

  return (
    <div className="sai-overlay">
      <div className="sai-card">

        {/* ── Header ── */}
        <div className="sai-header">
          <div className="sai-header-left">
            <div className="sai-header-icon">
              <BsStars size={20} color="#fff" />
            </div>
            <div>
              <p className="sai-header-title">AI Service Assistant</p>
              <p className="sai-header-sub">
                {isDone ? "Content ready — review and edit below"
                  : isGenerating ? `Generating content for "${serviceName}"…`
                  : `2 quick questions → AI fills everything`}
              </p>
            </div>
          </div>
          <button className="sai-close-btn" onClick={onClose}>
            <AiOutlineCloseCircle size={18} />
          </button>
        </div>

        {/* ── Progress bar ── */}
        <div className="sai-progress-track">
          <div className="sai-progress-fill" style={{ width: `${progress * 100}%` }} />
        </div>

        {/* ── Body ── */}
        <div className="sai-body">

          {/* ── GENERATING STATE ── */}
          {isGenerating && (
            <div className="sai-loading-box">
              <div className="sai-spinner" />
              <p className="sai-loading-title">Writing your service profile…</p>
              <p className="sai-loading-sub">
                Crafting description &amp; features for <strong>"{serviceName}"</strong>
              </p>
              <div className="sai-loading-bar-track">
                <div className="sai-loading-bar-fill" />
              </div>
              <p style={{ fontSize:11, color:"#c4c9d4", marginBottom:10 }}>
                Generating features like…
              </p>
              <div className="sai-preview-pills">
                {previewFeats.map((f,i) => (
                  <span key={i} className="sai-preview-pill" style={{ animationDelay:`${i*0.08}s` }}>
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── DONE / SUCCESS STATE ── */}
          {isDone && !isGenerating && (
            <div className="sai-success-box">
              <BsCheckCircleFill className="sai-success-icon" />
              <p className="sai-success-title">Content Applied!</p>
              <p className="sai-success-sub">
                Your description and features have been filled in automatically.<br />
                Review and edit anything in the form below.
              </p>
              {parsedFeats.length > 0 && (
                <div className="sai-success-pills">
                  {parsedFeats.map((f,i) => (
                    <span key={i} className="sai-success-pill" style={{ animationDelay:`${i*0.07}s` }}>
                      ✓ {f}
                    </span>
                  ))}
                </div>
              )}
              <div style={{ display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" }}>
                {onNext && (
                  <button
                    className="sai-regen-btn"
                    onClick={() => { setStepIdx(0); setAnswers({}); setCurrentVal(""); }}
                  >
                    <FiRefreshCw size={13} /> Regenerate
                  </button>
                )}
                <button className="sai-done-btn" onClick={handleDone}>
                  <FiZap size={14} /> Review My Service
                </button>
              </div>
            </div>
          )}

          {/* ── QUESTION STEPS ── */}
          {!isGenerating && !isDone && currentStep && (
            <>
              {/* Step pill */}
              <div className="sai-step-pill">
                <span className="sai-step-dot" />
                Step {stepIdx + 1} of {totalUserSteps}
              </div>

              {/* What AI will generate — show only on step 0 */}
              {stepIdx === 0 && (
                <div className="sai-info-cards">
                  {[
                    { icon:"📝", label:"Description", desc:"2-3 sentence overview" },
                    { icon:"⚡", label:"5 Features", desc:"Key highlights" },
                    { icon:"🎯", label:"SEO-ready", desc:"Keyword optimised" },
                  ].map((c,i) => (
                    <div key={i} className="sai-info-card">
                      <div className="sai-info-card-icon">{c.icon}</div>
                      <p className="sai-info-card-label">{c.label}</p>
                      <p className="sai-info-card-desc">{c.desc}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Question */}
              <p className="sai-question">{currentStep.question}</p>
              <p className="sai-question-hint">{currentStep.hint}</p>

              {/* Input */}
              {currentStep.type === "textarea" ? (
                <textarea
                  ref={inputRef}
                  className="sai-textarea"
                  rows={3}
                  maxLength={currentStep.maxLen}
                  placeholder={currentStep.placeholder}
                  value={currentVal}
                  onChange={e => setCurrentVal(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              ) : (
                <input
                  ref={inputRef}
                  className="sai-input"
                  type="text"
                  maxLength={currentStep.maxLen}
                  placeholder={currentStep.placeholder}
                  value={currentVal}
                  onChange={e => setCurrentVal(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              )}
              <p className={`sai-char ${currentVal.length > currentStep.maxLen * 0.9 ? "warn" : ""}`}>
                {currentStep.maxLen - currentVal.length} left
              </p>

              {/* Actions */}
              <div className="sai-actions">
                <button className="sai-skip-btn" onClick={handleSkip}>
                  Skip this
                </button>
                <button
                  className="sai-next-btn"
                  onClick={handleNext}
                  disabled={!currentVal.trim()}
                >
                  {stepIdx < totalUserSteps - 1 ? "Next →" : <><FiZap size={14} /> Generate</>}
                </button>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
};

export default ServiceProfileWizardCard;